import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, Pencil, Paperclip } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FileUpload } from '@/components/ui/FileUpload';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { createPatrocinadorInput, updatePatrocinadorInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreatePatrocinadorInput = z.infer<typeof createPatrocinadorInput>;
type UpdatePatrocinadorInput = z.infer<typeof updatePatrocinadorInput>;

interface TabPatrocinadoresProps {
  eventoId: string;
  eventoNome: string;
  dataInicio: string;
}

const tiposPatrocinio = [
  { value: 'DIAMANTE', label: 'Diamante', className: 'bg-purple-100 text-purple-800' },
  { value: 'OURO', label: 'Ouro', className: 'bg-yellow-100 text-yellow-800' },
  { value: 'PRATA', label: 'Prata', className: 'bg-gray-100 text-gray-800' },
  { value: 'BRONZE', label: 'Bronze', className: 'bg-orange-100 text-orange-800' },
  { value: 'APOIO', label: 'Apoio', className: 'bg-blue-100 text-blue-800' },
];

const statusOptions = [
  { value: 'PENDENTE', label: 'Pendente', className: 'bg-yellow-50 text-yellow-800 border-yellow-300' },
  { value: 'RECEBIDO', label: 'Recebido', className: 'bg-green-50 text-green-800 border-green-300' },
  { value: 'PERMUTA', label: 'Permuta', className: 'bg-pink-50 text-pink-800 border-pink-300' },
] as const;

const statusClassMap: Record<string, string> = Object.fromEntries(
  statusOptions.map((s) => [s.value, s.className]),
);

export function TabPatrocinadores({ eventoId }: TabPatrocinadoresProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPatrocinador, setEditingPatrocinador] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.eventos.listPatrocinadores.useQuery({ id: eventoId, page, limit: 20 });
  const patrocinadores = data?.items;

  const invalidateAll = () => {
    utils.eventos.listPatrocinadores.invalidate();
    utils.eventos.getById.invalidate({ id: eventoId });
    utils.eventos.getFinanceiro.invalidate({ id: eventoId });
    utils.eventos.getEventoDashboard.invalidate({ id: eventoId });
    utils.livroCaixa.listLancamentos.invalidate({ eventoId });
    utils.livroCaixa.getResumoFinanceiro.invalidate({ eventoId });
    utils.livroCaixa.getBalancete.invalidate({ eventoId });
    utils.livroCaixa.getSaldosDiarios.invalidate({ eventoId });
    utils.contas.list.invalidate();
  };

  const addMutation = trpc.eventos.addPatrocinador.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseCreateModal();
    },
  });

  const updateMutation = trpc.eventos.updatePatrocinador.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseEditModal();
    },
  });

  const removeMutation = trpc.eventos.removePatrocinador.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  const alterarStatusMutation = trpc.eventos.alterarStatusPatrocinador.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  // Create form
  const createForm = useForm<CreatePatrocinadorInput>({
    resolver: zodResolver(createPatrocinadorInput),
    defaultValues: { eventoId, tipo: 'APOIO', statusPatrocinio: 'PENDENTE' },
  });

  // Edit form
  const editForm = useForm<UpdatePatrocinadorInput>({
    resolver: zodResolver(updatePatrocinadorInput),
  });

  const handleOpenCreateModal = () => {
    createForm.reset({ eventoId, nome: '', cnpjCpf: '', valor: '', tipo: 'APOIO', observacao: '', linkAnexo: '', statusPatrocinio: 'PENDENTE' });
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    createForm.reset();
  };

  const handleOpenEditModal = (patrocinador: NonNullable<typeof patrocinadores>[number]) => {
    editForm.reset({
      id: patrocinador.id,
      nome: patrocinador.nome,
      cnpjCpf: patrocinador.cnpjCpf || '',
      valor: patrocinador.valor,
      tipo: patrocinador.tipo as UpdatePatrocinadorInput['tipo'],
      observacao: patrocinador.observacao || '',
      linkAnexo: patrocinador.linkAnexo || '',
    });
    setEditingPatrocinador(patrocinador.id);
  };

  const handleCloseEditModal = () => {
    setEditingPatrocinador(null);
    editForm.reset();
  };

  const onCreateSubmit = (data: CreatePatrocinadorInput) => {
    addMutation.mutate(data);
  };

  const onEditSubmit = (data: UpdatePatrocinadorInput) => {
    updateMutation.mutate(data);
  };

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover este patrocinador?')) {
      removeMutation.mutate({ id });
    }
  };

  const handleAlterarStatus = (id: string, statusPatrocinio: 'PENDENTE' | 'RECEBIDO' | 'PERMUTA') => {
    const label = statusOptions.find((s) => s.value === statusPatrocinio)?.label || statusPatrocinio;
    if (window.confirm(`Alterar status para "${label}"?`)) {
      alterarStatusMutation.mutate({ id, statusPatrocinio });
    }
  };

  const formatCurrency = (value: string | number | null) => {
    if (!value) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const getTipoBadge = (tipo: string) => {
    const info = tiposPatrocinio.find((t) => t.value === tipo) || tiposPatrocinio[3]!;
    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${info.className}`}>
        {info.label}
      </span>
    );
  };

  const getStatusSelectClass = (status: string) => {
    return statusClassMap[status] || statusOptions[0].className;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const totalPatrocinios = patrocinadores?.filter((p) => p.statusPatrocinio !== 'PERMUTA').reduce((acc: number, p: { valor: string }) => acc + parseFloat(p.valor), 0) || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Total de patrocínios</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totalPatrocinios)}</p>
        </div>
        <Button onClick={handleOpenCreateModal} disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Patrocinador
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {patrocinadores && patrocinadores.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>CNPJ/CPF</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead className="w-12">Anexo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patrocinadores.map((patrocinador) => (
                  <TableRow key={patrocinador.id}>
                    <TableCell className="font-medium">{patrocinador.nome}</TableCell>
                    <TableCell>{patrocinador.cnpjCpf || '-'}</TableCell>
                    <TableCell>{getTipoBadge(patrocinador.tipo)}</TableCell>
                    <TableCell className="font-medium text-green-600">{formatCurrency(patrocinador.valor)}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <select
                          value={patrocinador.statusPatrocinio}
                          onChange={(e) => handleAlterarStatus(patrocinador.id, e.target.value as 'PENDENTE' | 'RECEBIDO' | 'PERMUTA')}
                          disabled={alterarStatusMutation.isPending}
                          className={`rounded-lg border px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary-500 ${getStatusSelectClass(patrocinador.statusPatrocinio)}`}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusSelectClass(patrocinador.statusPatrocinio)}`}>
                          {statusOptions.find((s) => s.value === patrocinador.statusPatrocinio)?.label || 'Pendente'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{patrocinador.observacao || '-'}</TableCell>
                    <TableCell>
                      {patrocinador.linkAnexo ? (
                        <a href={patrocinador.linkAnexo} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Ver recibo">
                          <Paperclip className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditModal(patrocinador)}
                          disabled={!canEdit}
                          className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                          title="Editar patrocinador"
                        >
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(patrocinador.id)}
                          disabled={!canEdit || removeMutation.isPending}
                          className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <p>Nenhum patrocinador cadastrado</p>
            </div>
          )}
          {data && (
            <div className="px-4 pb-2">
              <Pagination meta={data.meta} onPageChange={setPage} itemLabel="patrocinadores" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Modal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} title="Novo Patrocinador">
        <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4">
          <input type="hidden" {...createForm.register('eventoId')} />

          <Input label="Nome" {...createForm.register('nome')} error={createForm.formState.errors.nome?.message} />

          <Input label="CNPJ/CPF" {...createForm.register('cnpjCpf')} error={createForm.formState.errors.cnpjCpf?.message} />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valor"
              type="number"
              step="0.01"
              {...createForm.register('valor')}
              error={createForm.formState.errors.valor?.message}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                {...createForm.register('tipo')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {tiposPatrocinio.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              {...createForm.register('statusPatrocinio')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Observação</label>
            <textarea
              {...createForm.register('observacao')}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <FileUpload
            label="Recibo / Comprovante"
            value={createForm.watch('linkAnexo') || undefined}
            onUploadComplete={(url) => createForm.setValue('linkAnexo', url)}
            onClear={() => createForm.setValue('linkAnexo', '')}
          />

          <p className="text-sm text-gray-500">
            * Uma conta a receber será criada automaticamente no módulo Financeiro
            {createForm.watch('statusPatrocinio') === 'PERMUTA' && ' (permuta não contabiliza no livro caixa)'}
          </p>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={addMutation.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Editar */}
      <Modal isOpen={!!editingPatrocinador} onClose={handleCloseEditModal} title="Editar Patrocinador">
        <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
          <input type="hidden" {...editForm.register('id')} />

          <Input label="Nome" {...editForm.register('nome')} error={editForm.formState.errors.nome?.message} />

          <Input label="CNPJ/CPF" {...editForm.register('cnpjCpf')} error={editForm.formState.errors.cnpjCpf?.message} />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valor"
              type="number"
              step="0.01"
              {...editForm.register('valor')}
              error={editForm.formState.errors.valor?.message}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                {...editForm.register('tipo')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {tiposPatrocinio.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Observação</label>
            <textarea
              {...editForm.register('observacao')}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <FileUpload
            label="Recibo / Comprovante"
            value={editForm.watch('linkAnexo') || undefined}
            onUploadComplete={(url) => editForm.setValue('linkAnexo', url)}
            onClear={() => editForm.setValue('linkAnexo', '')}
          />

          <p className="text-sm text-gray-500">
            * As alterações serão refletidas automaticamente no módulo Financeiro
          </p>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
